<script setup lang="ts">
import { ref, computed } from 'vue';
import { AdminWidget } from '@/interfaces/Dashboard';
import ConnxField from '@/components/Molecules/Field/ConnxField.vue';
import ConnxIcon from '@/components/Atoms/ConnxIcon.vue';
import ConnxButton from '@/components/Atoms/ConnxButton.vue';
import { chartIconMap } from '@/lib/chart-helpers';
import { InputType } from '@/interfaces/InputFields';
import useDashboardStore from '@/stores/Dashboard/dashboard-store';
import ConnxEditor from '@/components/Molecules/Editor/ConnxEditor.vue';
import { useClipboard } from '@vueuse/core';
import { cloneDeep, isEqual } from 'lodash';
import useFlashMessageStore from '@/stores/flash-messages';
import sanitize from '@/lib/sanitize';

const props = defineProps<{ widget: AdminWidget }>();
const messageStore = useFlashMessageStore();
const dashboardStore = useDashboardStore();

const copySupportCode = async () => {
  await copy();
  messageStore.pushNotification({
    id: 'copied',
    type: 'success',
    message: 'Successfully copied support code to clipboard.',
  });
};

const errors = ref(new Map<string, string | null>());
const helpFieldTextExpanded = ref(false);
const isEditingHelpFieldText = ref(false);
const onClickHelpFieldTextEdit = () => {
  isEditingHelpFieldText.value = !isEditingHelpFieldText.value;
  if (isEditingHelpFieldText.value) {
    helpFieldTextExpanded.value = true;
  }
};

const selectedWidgetNewSettings = ref<AdminWidget>(cloneDeep(props.widget));

const hasChanges = computed(() => !isEqual(props.widget, selectedWidgetNewSettings.value));

const hasErrors = computed(() => errors.value.size > 0);
const source = computed(() => selectedWidgetNewSettings.value.widgetCode);
const { copy, copied, isSupported } = useClipboard({ source, legacy: true });

const saveChanges = async (): Promise<boolean | AdminWidget> => {
  //@TODO: Move to on input when we have a rich text editor.
  selectedWidgetNewSettings.value = {
    ...selectedWidgetNewSettings.value,
    helpFieldText: sanitize(selectedWidgetNewSettings.value.helpFieldText),
  };

  const success = await dashboardStore.updateAdminWidget(selectedWidgetNewSettings.value);
  if (success) {
    return selectedWidgetNewSettings.value;
  }
  return false;
};

const onFieldError = ({ name, error }: { name: string; error: string | null }) => {
  if (error) {
    errors.value.set(name, error);
  } else {
    errors.value.delete(name);
  }
};

const displayTypes = computed(() => {
  const properties = selectedWidgetNewSettings.value?.defaultJsonProperties;
  const availableTypes = properties?.availableDisplayTypes?.map((t) => t.toLowerCase()) || [];

  return Array.from(chartIconMap.entries()).filter(([type]) => availableTypes.includes(type));
});

const setCurrentDisplayType = (w: AdminWidget, type: string) => {
  if (!selectedWidgetNewSettings.value) return;
  const isTable = type === 'table';
  w.defaultVisualType = isTable ? w.defaultVisualType : type;
  w.defaultJsonProperties.displayedAsTable = isTable;
};

const showTooltip = ref(false);

function onMouseEnter() {
  showTooltip.value = true;
}

function onMouseLeave() {
  showTooltip.value = false;
}

const hideTimeout = ref<ReturnType<typeof setTimeout> | null>(null);
const hideWithDelay = () => {
  hideTimeout.value = setTimeout(() => {
    showTooltip.value = false;
  }, 200);
};

const clearHideDelay = () => {
  if (hideTimeout.value) {
    clearTimeout(hideTimeout.value);
    hideTimeout.value = null;
  }
};

defineExpose({ saveChanges, hasChanges, hasErrors });
</script>
<template>
  <connx-field
    name="title"
    :type="InputType.Text"
    required
    :maxlength="100"
    @update:error="onFieldError"
    v-model="selectedWidgetNewSettings.widgetName"
  >
    <template #label>
      <div class="tw-flex tw-gap-x-2">
        Widget Title
        <span class="tw-text-connx-danger">*</span>

        <div class="tw-relative">
          <div
            class="tw-inline-flex tw-items-center tw-cursor-pointer"
            @mouseenter="showTooltip = true"
            @mouseleave="hideWithDelay"
          >
            <connx-icon name="far fa-info-circle" class="tw-h-4 tw-w-4" />
          </div>

          <div
            v-show="showTooltip"
            @mouseenter="clearHideDelay"
            @mouseleave="hideWithDelay"
            class="tw-absolute tw-left-0 tw-w-96 tw-p-2 tw-shadow tw-rounded tw-z-1200 tw-bg-connx-white tw-text-connx-grey"
          >
            <div class="tw-flex tw-gap-x-2 tw-justify-center tw-items-center">
              <label class="Field__label tw-mb-0 tw-text-nowrap">Support Key :</label>
              <input type="text" class="Field peer Field--text tw-mb-0 tw-w-48" :value="source" disabled />
              <connx-button
                type="button"
                button-style="blank"
                button-size="sm"
                @click="copySupportCode"
                class="tw-text-connx-grey"
              >
                <connx-icon :name="copied ? 'far fa-check' : 'far fa-copy'" class="tw-h-4 tw-w-4" />
              </connx-button>
            </div>
          </div>
        </div>
      </div>
    </template>
  </connx-field>
  <connx-field
    name="subtitle"
    :type="InputType.Text"
    :maxlength="100"
    label="Subheading"
    v-model="selectedWidgetNewSettings.subtitle"
  ></connx-field>
  <connx-field
    name="description"
    :type="InputType.TextArea"
    :maxlength="500"
    @update:error="onFieldError"
    label="Description"
    v-model="selectedWidgetNewSettings.longDescription"
  ></connx-field>
  <label class="Field__label" for="help">Help Text</label>
  <connx-editor
    v-model="selectedWidgetNewSettings.helpFieldText"
    name="help"
    id="help"
    ref="helpInput"
    @update:error="onFieldError"
    :expanded="helpFieldTextExpanded"
    :disabled="!isEditingHelpFieldText"
    @update:expanded="(val : boolean) => (helpFieldTextExpanded = val)"
  ></connx-editor>
  <div class="tw-flex tw-justify-between tw-mb-4">
    <div>
      <connx-button
        button-style="blank"
        unpadded
        @click="() => (helpFieldTextExpanded = !helpFieldTextExpanded)"
        class="tw-text-connx-primary tw-text-semibold tw-px-0 tw-py-1"
      >
        {{ helpFieldTextExpanded ? 'Show less' : 'Show more' }}
      </connx-button>
    </div>
    <div v-if="!isEditingHelpFieldText" class="tw-flex tw-gap-x-2 tw-items-center">
      <connx-button
        button-style="blank"
        unpadded
        @click="onClickHelpFieldTextEdit"
        class="tw-text-connx-primary tw-px-0 tw-py-1"
      >
        Click to edit
      </connx-button>
    </div>
  </div>
  <connx-field
    name="isInUse"
    :type="InputType.Checkbox"
    v-model="selectedWidgetNewSettings.isInUse"
    inline
    @change="selectedWidgetNewSettings.isInUse = $event.target.checked"
    >In Use</connx-field
  >

  <section v-if="displayTypes.length">
    <h5 class="tw-mb-4 Field__label tw-text-base">Visual Type</h5>
    <div class="tw-flex tw-gap-4 tw-flex-wrap tw-justify-start tw-pb-4">
      <button
        v-for="[type, icon] in displayTypes"
        :title="icon.label"
        :key="type"
        @click="setCurrentDisplayType(selectedWidgetNewSettings, type)"
        class="tw-background-transparent tw-outline-none tw-w-20 tw-h-20 tw-rounded-md tw-border tw-border-solid tw-flex tw-items-center tw-justify-center hover:tw-text-connx-primary hover:tw-border-connx-primary hover:tw-border-2"
        :class="
          (selectedWidgetNewSettings.defaultVisualType === type &&
            !selectedWidgetNewSettings.defaultJsonProperties.displayedAsTable) ||
          (selectedWidgetNewSettings.defaultJsonProperties.displayedAsTable && type === 'table')
            ? 'tw-border-connx-primary tw-text-connx-primary tw-border-2'
            : 'tw-border-connx-grey-tint-70 tw-text-connx-grey-tint-70'
        "
      >
        <connx-icon :name="icon.icon" :class="icon.class" class="tw-w-12 tw-h-auto"></connx-icon>
      </button>
    </div>
  </section>
</template>
